import { SPAN } from './constants';
import { cheb, cx, cy, wireLength } from './geometry';
import type { Point, Puzzle, ScoredPuzzle, Stats, Tier } from './types';

/**
 * Reference wire floor: an MST over plant + houses using straight-line
 * distance, ignoring the span rule entirely.
 *
 * It's a stable, cheap denominator for the MW rating rather than a true lower
 * bound — towers act as Steiner points, so an excellent layout can dip a few
 * percent below it. That's what {@link mwFor}'s clamp at 100 is for.
 */
export function mstFloor(puzzle: Puzzle): number {
  const T = [puzzle.plant, ...puzzle.houses];
  const inTree = [0];
  const dist = T.map((t, i) => (i ? Math.hypot(cx(t) - cx(T[0]), cy(t) - cy(T[0])) : 0));
  let total = 0;
  while (inTree.length < T.length) {
    let bi = -1;
    let bd = 1e18;
    for (let i = 0; i < T.length; i++) {
      if (!inTree.includes(i) && dist[i] < bd) {
        bd = dist[i];
        bi = i;
      }
    }
    total += bd;
    inTree.push(bi);
    for (let i = 0; i < T.length; i++) {
      if (!inTree.includes(i)) {
        dist[i] = Math.min(dist[i], Math.hypot(cx(T[i]) - cx(T[bi]), cy(T[i]) - cy(T[bi])));
      }
    }
  }
  return total;
}

/**
 * The same tree {@link mstFloor} measures, as the edges it is made of.
 *
 * Not drawn anywhere: it is geometry, not a layout, and it was briefly
 * rendered on the board before that was cut for teaching the wrong thing (a
 * line between two houses is a connection the span rule forbids). What reads
 * it now is the topology classifier, which measures how close a reference's
 * crossing sits to this tree.
 *
 * Deliberately a second pass rather than a refactor of {@link mstFloor} to
 * return both: that function's floating-point summation order is baked into
 * every MW score, and a tidier shared implementation that summed in a
 * different order would move scores by a hair across the whole archive.
 * Duplication is the cheaper of the two prices.
 */
export function mstEdges(puzzle: Puzzle): [Point, Point][] {
  const T = [puzzle.plant, ...puzzle.houses];
  const inTree = [0];
  const dist = T.map((t, i) => (i ? Math.hypot(cx(t) - cx(T[0]), cy(t) - cy(T[0])) : 0));
  const from = T.map(() => 0);
  const edges: [Point, Point][] = [];

  while (inTree.length < T.length) {
    let bi = -1;
    let bd = 1e18;
    for (let i = 0; i < T.length; i++) {
      if (!inTree.includes(i) && dist[i] < bd) {
        bd = dist[i];
        bi = i;
      }
    }
    edges.push([T[from[bi]], T[bi]]);
    inTree.push(bi);
    for (let i = 0; i < T.length; i++) {
      if (!inTree.includes(i)) {
        const d = Math.hypot(cx(T[i]) - cx(T[bi]), cy(T[i]) - cy(T[bi]));
        if (d < dist[i]) {
          dist[i] = d;
          from[i] = bi;
        }
      }
    }
  }
  return edges;
}

/**
 * Physical accounting for a tower layout.
 *
 * This MUST mirror the renderer's rules — see `network()`. Every node within
 * span of an energized node is itself energized; every such pair strings wire;
 * each house takes its single best drop, preferring a live source and then the
 * nearest one.
 */
export function statsFor(puzzle: Puzzle, towers: Point[]): Stats {
  const nodes = [puzzle.plant, ...towers];
  const energized = new Set([0]);
  let changed = true;
  while (changed) {
    changed = false;
    for (let i = 1; i < nodes.length; i++) {
      if (energized.has(i)) continue;
      for (const j of energized) {
        if (cheb(nodes[i], nodes[j]) <= SPAN) {
          energized.add(i);
          changed = true;
          break;
        }
      }
    }
  }

  let wire = 0;
  for (let i = 0; i < nodes.length; i++) {
    for (let j = i + 1; j < nodes.length; j++) {
      if (cheb(nodes[i], nodes[j]) <= SPAN) wire += wireLength(nodes[i], nodes[j]);
    }
  }

  let lit = 0;
  for (const h of puzzle.houses) {
    let best: Point | null = null;
    let bestD = 1e9;
    let isLit = false;
    for (let i = 0; i < nodes.length; i++) {
      const n = nodes[i];
      if (cheb(n, h) <= SPAN) {
        const d = (n.r - h.r) ** 2 + (n.c - h.c) ** 2;
        const e = energized.has(i);
        if (e && !isLit) {
          isLit = true;
          best = n;
          bestD = d;
        } else if (e === isLit && d < bestD) {
          best = n;
          bestD = d;
        }
      }
    }
    if (best) wire += wireLength(best, h);
    if (isLit) lit++;
  }

  return { ok: lit === puzzle.houses.length, lit, wire };
}

/** Megawatts delivered: the wire floor as a percentage of wire actually strung. */
export function mwFor(puzzle: ScoredPuzzle, wire: number): number {
  return Math.max(1, Math.min(100, Math.round((100 * puzzle.mst) / wire)));
}

/** Rank a layout against the best known grid. */
export function tierFor(refWire: number, wire: number): Tier {
  const r = refWire / wire;
  const [emoji, name] =
    r >= 0.999
      ? ['🏆', 'Superconductor']
      : r >= 0.96
        ? ['🥇', 'Master Lineworker']
        : r >= 0.9
          ? ['🥈', 'Journeyman Lineworker']
          : ['🥉', 'Apprentice Lineworker'];
  return { emoji, name, short: name.replace(' Lineworker', '') };
}
